/* eslint-disable camelcase -- GA4 event parameter names use snake_case. */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import registry from '@/data/site-registry.json';
import { analyticsEnabled, sendAnalytics } from './analytics';

// Browser-history page views and automatic form interactions are OFF for
// stream 5535244228 (saved in GA and verified with Tag Assistant 2026-10-22).
// This component is the sole page_view owner. Query strings and hashes are excluded.
export default function AnalyticsRuntime() {
	const { pathname } = useLocation();
	const previousPath = useRef('');
	useEffect(() => {
		const page = registry.pages.find((item) => item.path === pathname);
		const disabled = !page || !analyticsEnabled();
		(window as unknown as Record<string, unknown>)[
			`ga-disable-${process.env.REACT_APP_GA_MEASUREMENT_ID}`
		] = disabled;
		if (disabled || !page) return;
		const timers = new Map<Element, ReturnType<typeof setTimeout>>();
		const seen = new Set<string>();
		const sessionKey = 'mrd-analytics-sections-v1';
		let visit: { expires: number; seen: string[] } = {
			expires: 0,
			seen: []
		};
		try {
			visit =
				JSON.parse(sessionStorage.getItem(sessionKey) || 'null') ||
				visit;
		} catch {
			/* optional storage */
		}
		if (!Array.isArray(visit.seen) || visit.expires < Date.now())
			visit = { expires: Date.now() + 1800000, seen: [] };
		visit.seen.forEach((id) => seen.add(id));
		const persist = () => {
			try {
				sessionStorage.setItem(
					sessionKey,
					JSON.stringify({
						expires: Date.now() + 1800000,
						seen: Array.from(seen).slice(-2000)
					})
				);
			} catch {
				/* optional storage */
			}
		};
		// Helmet and route content have committed before this deferred initial measurement.
		const referrer = previousPath.current ? registry.siteUrl + previousPath.current : safeReferrer(document.referrer);
		previousPath.current = pathname;
		let pageSent = false;
		const recordPage = () => {
			if (pageSent || document.querySelector('link[rel="canonical"]')?.getAttribute('href') !== registry.siteUrl + page.path) return;
			pageSent = true;
			sendAnalytics('page_view', { page_id: page.id, page_location: registry.siteUrl + page.path, page_title: document.title, page_referrer: referrer });
			pageHead.disconnect();
		};
		// Lazy SPA content can commit later than the router. Wait for its canonical,
		// and never depend on rAF, which pauses in a background tab.
		const pageHead = new MutationObserver(recordPage);
		pageHead.observe(document.head, { childList: true, subtree: true, attributes: true, characterData: true });
		const pageTimer = setTimeout(recordPage, 0);
		const sectionId = (el: Element) =>
			el.getAttribute('data-analytics-section') || el.id;
		const selector =
			'section[id],article[id],[data-analytics-section],#mr-main-one';
		const cancel = (el: Element) => {
			clearTimeout(timers.get(el));
			timers.delete(el);
		};
		const isVisible = (el: Element) => {
			if (document.visibilityState !== 'visible' || !el.isConnected || document.querySelector('link[rel="canonical"]')?.getAttribute('href') !== registry.siteUrl + page.path)
				return false;
			let ancestor: Element | null = el;
			while (ancestor) {
				const style = getComputedStyle(ancestor);
				if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0' || ancestor.getAttribute('aria-hidden') === 'true') return false;
				ancestor = ancestor.parentElement;
			}
			const rect = el.getBoundingClientRect();
			const visible =
				Math.min(rect.bottom, window.innerHeight) -
				Math.max(0, rect.top);
			return (
				rect.width > 0 &&
				rect.height > 0 &&
				visible >=
					Math.min(rect.height * 0.5, window.innerHeight * 0.25)
			);
		};
		const consider = (el: Element) => {
			const id = sectionId(el),
				key = `${page.id}:${id}`;
			if (
				!/^[a-zA-Z][a-zA-Z0-9_-]{0,99}$/.test(id) ||
				seen.has(key) ||
				!isVisible(el)
			) {
				cancel(el);
				return;
			}
			if (timers.has(el)) return;
			timers.set(
				el,
				setTimeout(() => {
					timers.delete(el);
					if (!isVisible(el) || seen.has(key)) return;
					seen.add(key);
					persist();
					sendAnalytics('section_view', {
						page_id: page.id,
						section_id: id
					});
				}, 1000)
			);
		};
		const elements = new Set<Element>();
		const observer =
			typeof IntersectionObserver === 'undefined'
				? null
				: new IntersectionObserver(
						(entries) =>
							entries.forEach((entry) => consider(entry.target)),
						{ threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
				  );
		const discover = () =>
			document.querySelectorAll(selector).forEach((el) => {
				if (!elements.has(el)) {
					elements.add(el);
					observer?.observe(el);
				}
			});
		discover();
		const mutations = new MutationObserver(discover);
		mutations.observe(document.getElementById('root') || document.body, {
			childList: true,
			subtree: true
		});
		// Check scroll as well: tall sections can cross the visibility boundary without
		// crossing an IntersectionObserver ratio threshold. One rAF per scroll frame.
		let scrollFrame = 0;
		const recheck = () => {
			if (scrollFrame) return;
			scrollFrame = requestAnimationFrame(() => {
				scrollFrame = 0;
				elements.forEach(consider);
			});
		};
		const click = (event: MouseEvent) => {
			const el =
				event.target instanceof Element
					? event.target.closest('[data-analytics-cta]')
					: null;
			const id = el?.getAttribute('data-analytics-cta');
			if (!id || !/^[a-z][a-z0-9_-]{0,99}$/.test(id)) return;
			const section = el?.closest(selector);
			sendAnalytics('cta_click', {
				page_id: page.id,
				cta_id: id,
				...(section ? { section_id: sectionId(section) } : {})
			});
		};
		let formStarted = false;
		const formStart = (event: Event) => {
			if (
				formStarted ||
				!(event.target instanceof Element) ||
				!event.target.closest('[data-analytics-form="project_inquiry"]')
			)
				return;
			formStarted = true;
			sendAnalytics('form_start', {
				page_id: page.id,
				form_id: 'project_inquiry'
			});
		};
		const visibility = () => {
			if (document.hidden) elements.forEach(cancel);
			else recheck();
		};
		// Capture before React Router can commit the destination route.
		document.addEventListener('click', click, true);
		document.addEventListener('input', formStart);
		document.addEventListener('change', formStart);
		document.addEventListener('visibilitychange', visibility);
		window.addEventListener('scroll', recheck, { passive: true });
		window.addEventListener('resize', recheck, { passive: true });
		recheck();
		return () => {
			clearTimeout(pageTimer);
			pageHead.disconnect();
			cancelAnimationFrame(scrollFrame);
			mutations.disconnect();
			observer?.disconnect();
			elements.forEach(cancel);
			document.removeEventListener('click', click, true);
			document.removeEventListener('input', formStart);
			document.removeEventListener('change', formStart);
			document.removeEventListener('visibilitychange', visibility);
			window.removeEventListener('scroll', recheck);
			window.removeEventListener('resize', recheck);
		};
	}, [pathname]);
	return null;
}

function safeReferrer(value: string) {
	try {
		const url = new URL(value);
		return /^https?:$/.test(url.protocol) ? url.origin : '';
	} catch {
		return '';
	}
}
